import { useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import { isApiError } from '@/api/errors'
import type { CatalogAvatarDto } from '@/api/types'
import {
  avatarCategoriesQuery,
  avatarsQuery,
  useCreateAvatar,
  useDeleteAvatar,
  useUpdateAvatar,
} from '@/features/platform/platform.queries'
import { AvatarCategoriesDialog } from '@/features/platform/AvatarCategoriesDialog'
import { withDeclaredContentType } from '@/features/platform/assetKinds'
import { AvatarEditDialog } from '@/features/platform/AvatarEditDialog'
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  Dialog,
  PageHeader,
  SearchInput,
  SelectField,
  TextField,
  useToast,
  type Column,
} from '@/ui'
import { IconAdd, IconCategories, IconDelete, IconEdit, IconHidden, IconVisible } from '@/icons'

const searchSchema = z.object({
  q: z.string().default(''),
  categoryKey: z.string().optional(),
  isActive: z.boolean().optional(),
})

export const Route = createFileRoute('/_authed/avatars/')({
  validateSearch: searchSchema,
  component: AvatarsPage,
})

function AvatarsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const query = useInfiniteQuery(
    avatarsQuery({
      q: search.q,
      ...(search.categoryKey ? { categoryKey: search.categoryKey } : {}),
      ...(search.isActive !== undefined ? { isActive: search.isActive } : {}),
    }),
  )
  const categories = useQuery(avatarCategoriesQuery)
  const { items, total } = flattenPages<CatalogAvatarDto>(query.data)

  const categoryNames = new Map(
    (categories.data?.items ?? []).map((category) => [category.key, category.name]),
  )

  const update = useUpdateAvatar()
  const remove = useDeleteAvatar()

  const [isAdding, setIsAdding] = useState(false)
  const [deleting, setDeleting] = useState<CatalogAvatarDto | null>(null)
  /*
   * The KEY of the avatar being edited, not a snapshot of it.
   *
   * Holding the object froze it at the moment the row was clicked, so after uploading a new
   * gallery picture the dialog kept rendering the old `imageUrl` and the save looked like it had
   * silently failed. Resolving from `items` on every render means a refetch reaches the dialog.
   */
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const editing = items.find((avatar) => avatar.key === editingKey) ?? null
  const [isManagingCategories, setIsManagingCategories] = useState(false)

  const categoryOptions = (categories.data?.items ?? []).map((category) => ({
    value: category.key,
    label: category.name,
  }))

  const columns: Column<CatalogAvatarDto>[] = [
    {
      id: 'avatar',
      header: t('avatars.columnAvatar'),
      cell: (avatar) => (
        <div className="flex min-w-0 items-center gap-2.5">
          {avatar.imageUrl ? (
            <img
              src={avatar.imageUrl}
              alt=""
              width={32}
              height={32}
              loading="lazy"
              className="size-8 shrink-0 rounded object-cover"
            />
          ) : (
            <span aria-hidden className="size-8 shrink-0 rounded bg-surface-sunken" />
          )}
          <p className="min-w-0 truncate font-medium text-text">{avatar.name}</p>
        </div>
      ),
    },
    {
      id: 'category',
      header: t('avatars.columnCategory'),
      cell: (avatar) => {
        // The list DTO carries only the key; the human name lives on the categories query,
        // which this page has already loaded for the filter.
        const name = categoryNames.get(avatar.categoryKey)

        return name ? (
          <Badge>{name}</Badge>
        ) : (
          <span className="text-text-muted">{avatar.categoryKey || t('common.notSet')}</span>
        )
      },
    },
    {
      id: 'status',
      header: t('avatars.columnStatus'),
      cell: (avatar) => (
        <div className="flex items-center gap-1.5">
          <Badge tone={avatar.isActive ? 'success' : 'neutral'} hasDot>
            {avatar.isActive ? t('avatars.badgeActive') : t('avatars.badgeInactive')}
          </Badge>
          {avatar.isPremium ? <Badge tone="warning">{t('avatars.badgePremium')}</Badge> : null}
        </div>
      ),
    },
    {
      id: 'default',
      header: t('avatars.columnDefault'),
      cell: (avatar) =>
        avatar.isDefault ? (
          <Badge tone="primary">{t('avatars.badgeDefault')}</Badge>
        ) : (
          <span className="text-text-muted">{t('common.notSet')}</span>
        ),
    },
    {
      id: 'order',
      header: t('avatars.columnOrder'),
      align: 'end',
      isSecondary: true,
      cell: (avatar) => (
        <span data-numeric className="text-text-secondary">
          {avatar.sortOrder}
        </span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      width: '3rem',
      cell: (avatar) => (
        <ActionMenu
          actions={[
            {
              id: 'edit',
              label: t('avatars.editTitle'),
              icon: <IconEdit size={15} />,
              onSelect: () => {
                setEditingKey(avatar.key)
              },
            },
            {
              id: 'visibility',
              label: avatar.isActive ? t('avatars.badgeInactive') : t('avatars.badgeActive'),
              icon: avatar.isActive ? <IconHidden size={15} /> : <IconVisible size={15} />,
              onSelect: () => {
                update.mutate(
                  { avatarKey: avatar.key, isActive: !avatar.isActive },
                  {
                    onSuccess: () => {
                      toast.success(t('avatars.updated'))
                    },
                    onError: (error) => {
                      toast.fromError(error, t('state.errorTitle'))
                    },
                  },
                )
              },
            },
            {
              id: 'delete',
              label: t('common.delete'),
              icon: <IconDelete size={15} />,
              isDestructive: true,
              onSelect: () => {
                setDeleting(avatar)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('avatars.title')}
        description={t('avatars.subtitle')}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<IconCategories size={15} />}
              onClick={() => {
                setIsManagingCategories(true)
              }}
            >
              {t('avatars.categories')}
            </Button>
            <Button
              variant="primary"
              icon={<IconAdd size={15} />}
              onClick={() => {
                setIsAdding(true)
              }}
            >
              {t('avatars.add')}
            </Button>
          </>
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput
            value={search.q}
            onChange={(q) => {
              void navigate({ search: (prev) => ({ ...prev, q }) })
            }}
            placeholder={t('avatars.searchPlaceholder')}
            className="min-w-56 flex-1"
          />

          <label className="sr-only" htmlFor="category-filter">
            {t('avatars.category')}
          </label>
          <select
            id="category-filter"
            value={search.categoryKey ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({ ...prev, categoryKey: event.target.value || undefined }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          >
            <option value="">{t('common.all')}</option>
            {(categories.data?.items ?? []).map((category) => (
              <option key={category.key} value={category.key}>
                {category.name}
              </option>
            ))}
          </select>

          {total > 0 ? (
            <span data-numeric className="ml-auto text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(avatar) => avatar.key}
          caption={t('avatars.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          emptyTitle={search.q ? t('state.emptyFiltered') : t('avatars.empty')}
          emptyAction={
            !search.q ? (
              <Button
                variant="primary"
                size="sm"
                icon={<IconAdd size={14} />}
                onClick={() => {
                  setIsAdding(true)
                }}
              >
                {t('avatars.add')}
              </Button>
            ) : undefined
          }
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => {
            void query.fetchNextPage()
          }}
        />
      </Card>

      <AddAvatarDialog
        isOpen={isAdding}
        onClose={() => {
          setIsAdding(false)
        }}
        categories={categoryOptions}
      />

      <AvatarEditDialog
        key={editingKey ?? 'none'}
        avatar={editing}
        onClose={() => {
          setEditingKey(null)
        }}
        categories={categoryOptions}
      />

      <AvatarCategoriesDialog
        isOpen={isManagingCategories}
        onClose={() => {
          setIsManagingCategories(false)
        }}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null)
        }}
        onConfirm={() => {
          if (!deleting) return

          /*
           * The server refuses to delete an avatar somebody is wearing, and the list DTO does
           * not report a wearer count, so the client cannot know in advance. The body warns,
           * and a refusal surfaces the server's own reason rather than a generic failure —
           * hiding it instead is one click away in the row menu.
           */
          remove.mutate(deleting.key, {
            onSuccess: () => {
              toast.success(t('avatars.deleted'))
              setDeleting(null)
            },
            onError: (error) => {
              toast.fromError(error, t('avatars.deleteRefused'))
            },
          })
        }}
        title={t('avatars.deleteConfirm')}
        /* An avatar somebody is wearing cannot be deleted, so say that here rather than letting
           them press Delete and receive a refusal they could have been told about first. */
        body={t('avatars.deleteBody', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />
    </>
  )
}

function AddAvatarDialog({
  isOpen,
  onClose,
  categories,
}: {
  isOpen: boolean
  onClose: () => void
  categories: { value: string; label: string }[]
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const create = useCreateAvatar()

  const modelRef = useRef<HTMLInputElement>(null)
  const thumbnailRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [categoryKey, setCategoryKey] = useState('')
  const [error, setError] = useState<string | null>(null)

  function submit(event: React.SyntheticEvent) {
    event.preventDefault()

    const model = modelRef.current?.files?.[0]
    if (!model) {
      setError(t('common.requiredField'))
      return
    }

    const formData = new FormData()
    /*
     * Declared, not whatever the browser guessed. A `.glb` has no MIME type registered on
     * Windows, so the browser calls it `application/octet-stream` — which the avatar route
     * refuses for the same reason the asset route does: it validates a GLB by agreement between
     * the declared type, the sniffed bytes and the extension, and "unknown" agrees with nothing.
     */
    formData.append('model', withDeclaredContentType(model))
    formData.append('name', name)
    if (categoryKey) formData.append('categoryKey', categoryKey)

    const thumbnail = thumbnailRef.current?.files?.[0]
    if (thumbnail) formData.append('thumbnail', withDeclaredContentType(thumbnail))

    create.mutate(formData, {
      onSuccess: () => {
        toast.success(t('avatars.created'))
        setName('')
        setCategoryKey('')
        setError(null)
        onClose()
      },
      onError: (cause) => {
        // The GLB validator's message is far more useful than anything generic — it names the
        // actual problem, such as an external texture reference.
        setError(isApiError(cause) ? (cause.detail ?? cause.message) : t('state.errorTitle'))
      },
    })
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('avatars.addTitle')}
      isDismissable={!create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" form="add-avatar" type="submit" isLoading={create.isPending}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <form id="add-avatar" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t('avatars.fieldName')}
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />

        <SelectField
          label={t('avatars.fieldCategory')}
          value={categoryKey}
          options={[{ value: '', label: t('common.none') }, ...categories]}
          onChange={(event) => {
            setCategoryKey(event.target.value)
          }}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="avatar-model" className="text-[0.8125rem] font-medium text-text">
            {t('avatars.fieldModel')}
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          </label>
          <input
            id="avatar-model"
            ref={modelRef}
            type="file"
            accept=".glb,model/gltf-binary"
            required
            className="text-[0.8125rem] text-text-secondary file:mr-3 file:rounded-control file:border file:border-border file:bg-surface-raised file:px-3 file:py-1.5 file:text-[0.8125rem] file:text-text"
          />
          <p className="text-xs text-text-secondary">{t('avatars.modelHint')}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="avatar-thumbnail" className="text-[0.8125rem] font-medium text-text">
            {t('avatars.fieldThumbnail')}
          </label>
          <input
            id="avatar-thumbnail"
            ref={thumbnailRef}
            type="file"
            accept="image/*"
            className="text-[0.8125rem] text-text-secondary file:mr-3 file:rounded-control file:border file:border-border file:bg-surface-raised file:px-3 file:py-1.5 file:text-[0.8125rem] file:text-text"
          />
          <p className="text-xs text-text-secondary">{t('avatars.thumbnailHint')}</p>
        </div>

        {error ? (
          <p role="alert" className="text-[0.8125rem] text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
